import { ShieldCheck, PhoneCall, ArrowRight, Building2, House as Home, Hammer, Construction } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const accessCapabilities = [{
  title: "Commercial",
  icon: Building2,
  description: "Office, retail, and site projects"
}, {
  title: "Residential",
  icon: Home,
  description: "Extensions, painting, and maintenance"
}, {
  title: "Roofing",
  icon: Hammer,
  description: "Re-roofs, repairs, and chimneys"
}, {
  title: "Specialist Access",
  icon: Construction,
  description: "Cantilevers, bridging, and temporary roofs"
}];
export default function Richmond() {
  return <section id="herorichmond" data-nav="dark" className="relative overflow-hidden bg-secondary text-secondary-foreground py-20 md:py-28 lg:py-32">
      {/* Structural ambient pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-20%,rgba(5,32,250,0.22),transparent)] pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-primary/10 to-transparent pointer-events-none hidden lg:block" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Main Hero Copy & CTAs */}
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <Badge variant="outline" className="border-sidebar-border bg-sidebar-accent/50 text-secondary-foreground px-3 py-1 text-xs uppercase tracking-wider font-semibold">
                Richmond &amp; South West London
              </Badge>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-sidebar/80 px-3 py-1 rounded-md border border-sidebar-border">
                <ShieldCheck className="size-3.5 text-primary" />
                <span>NASC &amp; CHAS accredited</span>
              </div>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-secondary-foreground mb-6 font-default">
              Scaffolding in Richmond
            </h1>

            <p className="text-lg sm:text-xl text-secondary-foreground/80 max-w-2xl mb-8 leading-relaxed">
              BMF Scaffolding provides commercial, residential, roofing, solar and specialist scaffolding in Richmond and the surrounding South West London area, including TW9 and TW10. Safe, fully compliant access solutions engineered for contractors, developers, and homeowners.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto mb-10">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-md">
                <Link to="/ContactRequestQuotePage" className="inline-flex items-center justify-center gap-2">
                  <span>Request a quote</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-sidebar-border bg-sidebar-accent/60 text-secondary-foreground hover:bg-sidebar-accent hover:text-secondary-foreground font-semibold shadow-xs">
                <a href="tel:01344929900" className="inline-flex items-center justify-center gap-2 text-secondary-foreground hover:text-secondary-foreground">
                  <PhoneCall className="size-4 text-primary" />
                  <span>Call 01344 929900</span>
                </a>
              </Button>
            </div>

            {/* Credibility Highlights */}
            <div className="pt-6 border-t border-sidebar-border/60 w-full flex flex-wrap items-center gap-y-3 gap-x-8 text-xs sm:text-sm text-secondary-foreground/70">
              <div className="flex items-center gap-2">
                <span className="size-2 rounded-full bg-primary" />
                <span>20+ years operational experience</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="size-2 rounded-full bg-primary" />
                <span>TG20:21 &amp; SG4:22 safety standards</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="size-2 rounded-full bg-primary" />
                <span>Fully insured &amp; local council permits</span>
              </div>
            </div>
          </div>

          {/* Service Pillar Grid */}
          <div className="lg:col-span-5">
            <div className="bg-sidebar/90 border border-sidebar-border rounded-xl p-6 sm:p-8 shadow-xl relative backdrop-blur-xs">
              <div className="mb-5 pb-4 border-b border-sidebar-border">
                <h2 className="text-lg font-semibold text-secondary-foreground font-default">
                  Core access capabilities
                </h2>
                <p className="text-xs text-muted-foreground mt-1">
                  Engineered and erected to current safety specifications
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3.5">
                {accessCapabilities.map((item, i) => {
                const Icon = item.icon;
                return <div key={item.title} data-index={i} className="group flex items-start gap-3.5 p-3.5 rounded-lg bg-sidebar-accent/40 border border-sidebar-border/60 transition-[background-color,border-color] hover:bg-sidebar-accent hover:border-primary/40">
                      <div className="p-2.5 rounded-md bg-primary/10 border border-primary/20 text-primary shrink-0 transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                        <Icon className="size-5" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-secondary-foreground">
                          {item.title}
                        </div>
                        <p className="text-xs text-secondary-foreground/70 mt-0.5">
                          {item.description}
                        </p>
                      </div>
                    </div>;
              })}
              </div>

              <div className="mt-6 pt-4 border-t border-sidebar-border flex items-center justify-between text-xs text-secondary-foreground/80">
                <span>Need design drawings or pavement licenses?</span>
                <Link to="/ContactRequestQuotePage" className="text-primary font-medium hover:underline inline-flex items-center gap-1">
                  <span>Ask our team</span>
                  <ArrowRight className="size-3" />
                </Link>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}